import { emit, events } from '@estee/elc-service-bus';
import { observable, computed, action } from 'mobx';
import { ConfigStore } from '~setup/ConfigStore';
import { ProductRepository } from '~repositories/ProductRepository';
import * as Constants from '~constants/PowerReviews';
import { IRatingsReviewsConfig } from '~interfaces/IRating';
import { RatingsLoadedUseCase } from '~use-cases/RatingsLoadedUseCase';
import { IProductReviews } from '~exported/product-reviews/ProductReviews';
import { RatingsReviewsBaseController } from './RatingsReviewsBaseController';
import { REVIEW_CATEGORY } from '~constants/ProductReview';

export class ProductReviewsViewController extends RatingsReviewsBaseController {
    @observable public isLoading: boolean = true;
    @observable public config: IRatingsReviewsConfig;
    @observable public productRepository: ProductRepository;
    @observable public powerReviewsScript: HTMLScriptElement;
    @observable public activeTab: string = 'tab-1';

    constructor(configStore: ConfigStore, productRepository: ProductRepository) {
        super();
        this.config = configStore.config;
        this.productRepository = productRepository;
    }

    public initialize(props: IProductReviews) {
        const { config } = props;
        this.config = { ...this.config, ...config };
    }

    @computed
    get data() {
        return {
            isLoading: this.isLoading,
            activeTab: this.activeTab
        };
    }

    @computed
    public get productId() {
        return this.productRepository.productId || this.config.productId;
    }

    @computed
    public get product() {
        return this.productRepository.getProductById(this.productId);
    }

    @computed
    public get numberOfReviewsValue(): number {
        return this.product && this.product.reviews && this.product.reviews.numberOfReviews;
    }

    @action
    public setPaginationType = (paginationType: string) => {
        this.productRepository.setPaginationType(paginationType);
    };

    @action
    public setPrDisclosureCode = (prDisclosureCode: string) => {
        this.productRepository.setPrDisclosureCode(prDisclosureCode);
    };

    @action
    public onTabClick = (event: React.MouseEvent<HTMLElement>) => {
        const target = <HTMLElement>event.target;
        this.activeTab = target.dataset.tab || '';
    };

    @action
    public loadReviews = (powerReviewsScript: HTMLScriptElement) => {
        this.isLoading = false;
        // tslint:disable-next-line: no-floating-promises
        this.loaded().then(() => {
            this.loadPowerReviewsScripts(powerReviewsScript);
            this.addReviewsEventHandlers({
                id: this.productId,
                name: this.product?.displayName || '',
                reviewCategory: REVIEW_CATEGORY
            });
        });
    };

    public handleProductReviewsUnmount = () => {
        this.removeReviewsEventHandlers();
    };

    @action
    public loadPowerReviewsScripts = (powerReviewsScript: HTMLScriptElement) => {
        const bodyFirstScript = <HTMLScriptElement>document.querySelector('body > script');
        const parentNode = <HTMLScriptElement>bodyFirstScript.parentNode;
        powerReviewsScript.type = 'text/javascript';
        powerReviewsScript.src = Constants.POWER_REVIEWS_SCRIPT;
        powerReviewsScript.defer = true;
        powerReviewsScript.async = true;
        powerReviewsScript.onload = () => {
            this.emitReviewsInit();
        };
        parentNode.insertBefore(powerReviewsScript, bodyFirstScript.nextSibling);

        this.powerReviewsScript = powerReviewsScript;
    };

    public showReviews = () => {
        const prScript = document.createElement('script');
        if (!this.powerReviewsScript) {
            this.loadReviews(prScript);
        }
    };

    public loaded = async () => {
        if (this.productId) {
            const ratingsLoadedUseCase = new RatingsLoadedUseCase(this.productId);
            await ratingsLoadedUseCase.execute();
        }

        return Promise.resolve();
    };

    public emitReviewsInit = () => {
        emit(events.SPP_POWER_REVIEWS_INIT, this.productId);
    };
}
